// Copyright (C) 2024 CVAT.ai Corporation
//
// SPDX-License-Identifier: MIT

import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import Card from 'antd/lib/card';
import Text from 'antd/lib/typography/Text';
import Button from 'antd/lib/button';
import Select from 'antd/lib/select';
import moment from 'moment';

import { updateJobQAStateAsync } from 'actions/labeling-runs-actions';
import { CombinedState } from 'reducers';

const QA_STATE_OPTIONS = [
    { value: 'new', label: 'New' },
    { value: 'labeling', label: 'Labeling' },
    { value: 'qa', label: 'QA' },
    { value: 'qa_complete', label: 'QA Complete' },
    { value: 'done', label: 'Done' },
    { value: 'sent_back', label: 'Sent Back' },
    { value: 'skip', label: 'Skip' },
];

function getCookie(name: string): string {
    const match = document.cookie.match(new RegExp(`(^|;\\s*)${name}=([^;]*)`));
    return match ? decodeURIComponent(match[2]) : '';
}

interface Props {
    job: any;
    runId: number;
    taskId: number;
    qaState: string;
}

export default function RunJobItemComponent({
    job, runId, taskId, qaState,
}: Props): JSX.Element {
    const dispatch = useDispatch();
    const history = useHistory();
    const updated = job.updatedDate ? moment(job.updatedDate).fromNow() : '';
    const currentUser = useSelector((state: CombinedState) => state.auth.user);
    const canManage = !!currentUser && (
        currentUser.isSuperuser || currentUser.isStaff || currentUser.groups.includes('admin')
    );

    const [assignee, setAssignee] = useState<{ id: number; username: string } | null>(
        job.assignee || null,
    );
    const [users, setUsers] = useState<{ value: number; label: string }[]>([]);

    useEffect(() => {
        if (!canManage) return;
        fetch('/api/users?page_size=500&is_active=true&org=', { credentials: 'same-origin' })
            .then((res) => res.json())
            .then((data) => {
                const options = (data.results || []).map((u: any) => ({
                    value: u.id,
                    label: u.username,
                }));
                setUsers(options);
            })
            .catch(() => {});
    }, [canManage]);

    const handleAssigneeChange = (userId: number | null): void => {
        fetch(`/api/jobs/${job.id}?org=`, {
            method: 'PATCH',
            credentials: 'same-origin',
            headers: {
                'Content-Type': 'application/json',
                'X-CSRFToken': getCookie('csrftoken'),
            },
            body: JSON.stringify({ assignee: userId }),
        })
            .then((res) => {
                if (!res.ok) throw new Error(`${res.status}`);
                return res.json();
            })
            .then((data) => {
                const a = data.assignee;
                setAssignee(a ? { id: a.id, username: a.username } : null);
            })
            .catch((err) => { console.error('Failed to update assignee:', err); });
    };

    return (
        <Card size='small' style={{ marginBottom: 8 }} className='cvat-run-job-item'>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div>
                    <Text
                        strong
                        style={{ cursor: 'pointer', color: '#1890ff', fontSize: 18 }}
                        onClick={() => history.push(`/tasks/${taskId}/jobs/${job.id}`)}
                    >
                        {`Job #${job.id}`}
                    </Text>
                    <br />
                    <Text type='secondary' style={{ fontSize: 16 }}>
                        {`Frames: ${job.startFrame} – ${job.stopFrame}`}
                    </Text>
                    {updated && (
                        <>
                            <br />
                            <Text type='secondary' style={{ fontSize: 16 }}>
                                {`Updated ${updated}`}
                            </Text>
                        </>
                    )}
                </div>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    {canManage ? (
                        <Select
                            allowClear
                            placeholder='nobody'
                            value={assignee?.id ?? undefined}
                            options={users}
                            style={{ width: 150 }}
                            onChange={(val: number | undefined) => handleAssigneeChange(val ?? null)}
                        />
                    ) : (
                        <Text type='secondary' style={{ fontSize: 14 }}>
                            {`Assignee: ${assignee ? assignee.username : 'nobody'}`}
                        </Text>
                    )}
                    <Select
                        value={qaState}
                        options={QA_STATE_OPTIONS}
                        size='large'
                        style={{ width: 150 }}
                        onChange={(val: string) => dispatch(updateJobQAStateAsync(runId, job.id, val) as any)}
                    />
                    <Button
                        type='primary'
                        size='large'
                        onClick={() => history.push(`/tasks/${taskId}/jobs/${job.id}`)}
                    >
                        Open
                    </Button>
                </div>
            </div>
        </Card>
    );
}
